import api from "../api";
import {
  Client,
  ClientCreatePayload,
  ClientsResponse,
  ClientUpdatePayload,
  PlatformUser,
  PlatformUsersResponse,
  PlatformUserCreateResponse,
  AdminFolderAccess,
  PlatformIntegrationsResponse,
  AdminChatBotProfile,
  BackendUser,
  BackendUsersResponse,
  AdminUserIntegrations,
  UserType,
} from "types/interfaces/adminUsers";

export interface GetAdminUsersParams {
  page?: number;
  search?: string;
  page_size?: number;
  role?: string;
  user_type?: UserType;
  client_id?: number;
  sort_by?:
    | "name"
    | "email"
    | "date_joined"
    | "last_login"
    | "folders"
    | "integrations"
    | "sftp"
    | "client_name"
    | "status";
  sort_order?: "asc" | "desc";
}

export interface GetClientsParams {
  page?: number;
  search?: string;
  page_size?: number;
  is_active?: boolean;
  sort_by?: "company_name" | "created_at" | "owner" | "status" | "users";
  sort_order?: "asc" | "desc";
  owner_id?: number;
}

export interface UpdateAdminUserPayload {
  id: number;
  username?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  user_type?: "internal" | "client";
  client_id?: number | null;
  is_data_storage_user?: boolean;
  is_integration_dashboard_user?: boolean;
  is_ai_agent_user?: boolean;
  is_calibrate_manager?: boolean;
  can_download_top_document_folders?: boolean;
  is_pass_reset?: boolean;
  owned_client_ids?: number[];
}

export interface CreateAdminUserPayload {
  username: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  user_type?: "internal" | "client";
  client_id?: number | null;
  is_data_storage_user?: boolean;
  is_integration_dashboard_user?: boolean;
  is_ai_agent_user?: boolean;
  can_download_top_document_folders?: boolean;
  owned_client_ids?: number[];
}

export interface AvailabilityResponse {
  available: boolean;
  message?: string;
}

const PLATFORM_BASE = "/api/admin/platform-users";
const BACKEND_BASE = "/api/admin/users";
const CLIENTS_BASE = "/api/admin/clients";

const adminUsersApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getAdminUsers: builder.query<PlatformUsersResponse, GetAdminUsersParams>({
      query: ({
        page = 1,
        search,
        page_size,
        role,
        user_type,
        client_id,
        sort_by,
        sort_order,
      } = {}) => {
        const params = new URLSearchParams();
        params.set("page", String(page));
        if (search) params.set("search", search);
        if (page_size) params.set("page_size", String(page_size));
        if (role) params.set("role", role);
        if (user_type) params.set("user_type", user_type);
        if (client_id) params.set("client_id", String(client_id));
        if (sort_by) params.set("sort_by", sort_by);
        if (sort_order) params.set("sort_order", sort_order);
        return `${PLATFORM_BASE}/?${params.toString()}`;
      },
      providesTags: ["AdminUsers"],
    }),

    getClients: builder.query<ClientsResponse, GetClientsParams>({
      query: ({
        page = 1,
        search,
        page_size,
        is_active,
        sort_by,
        sort_order,
        owner_id,
      } = {}) => {
        const params = new URLSearchParams();
        params.set("page", String(page));
        if (search) params.set("search", search);
        if (page_size) params.set("page_size", String(page_size));
        if (is_active !== undefined) params.set("is_active", String(is_active));
        if (sort_by) params.set("sort_by", sort_by);
        if (sort_order) params.set("sort_order", sort_order);
        if (owner_id) params.set("owner_id", String(owner_id));
        return `${CLIENTS_BASE}/?${params.toString()}`;
      },
      providesTags: ["AdminClients"],
    }),

    getClient: builder.query<Client, number>({
      query: (id) => `${CLIENTS_BASE}/${id}/`,
      providesTags: (_result, _error, id) => [{ type: "AdminClients", id }],
    }),

    createClient: builder.mutation<Client, ClientCreatePayload>({
      query: (body) => ({
        url: `${CLIENTS_BASE}/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["AdminClients"],
    }),

    updateClient: builder.mutation<
      Client,
      { id: number } & ClientUpdatePayload
    >({
      query: ({ id, ...body }) => ({
        url: `${CLIENTS_BASE}/${id}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "AdminClients", id },
        "AdminClients",
        "AdminUsers",
      ],
    }),

    archiveClient: builder.mutation<
      Client,
      { id: number; archiveDate?: string | null }
    >({
      query: ({ id, archiveDate }) => ({
        url: `${CLIENTS_BASE}/${id}/archive/`,
        method: "PATCH",
        body: archiveDate !== undefined ? { archive_date: archiveDate } : {},
      }),
      invalidatesTags: ["AdminClients", "AdminUsers"],
    }),

    checkClientCodeAvailability: builder.query<
      AvailabilityResponse,
      { value: string; excludeId?: number }
    >({
      query: ({ value, excludeId }) => {
        const params = new URLSearchParams();
        params.set("value", value);
        if (excludeId != null) params.set("exclude_id", String(excludeId));
        return `${CLIENTS_BASE}/check-code/?${params.toString()}`;
      },
    }),

    checkClientNameAvailability: builder.query<
      AvailabilityResponse,
      { value: string; excludeId?: number }
    >({
      query: ({ value, excludeId }) => {
        const params = new URLSearchParams();
        params.set("value", value);
        if (excludeId != null) params.set("exclude_id", String(excludeId));
        return `${CLIENTS_BASE}/check-name/?${params.toString()}`;
      },
    }),

    getAdminUser: builder.query<PlatformUser, number>({
      query: (id) => `${PLATFORM_BASE}/${id}/`,
      providesTags: (_result, _error, id) => [{ type: "AdminUsers", id }],
    }),

    checkPlatformUsernameAvailability: builder.query<
      AvailabilityResponse,
      { value: string; excludeId?: number }
    >({
      query: ({ value, excludeId }) => {
        const params = new URLSearchParams();
        params.set("value", value);
        if (excludeId != null) params.set("exclude_id", String(excludeId));
        return `${PLATFORM_BASE}/check-username/?${params.toString()}`;
      },
    }),

    checkPlatformEmailAvailability: builder.query<
      AvailabilityResponse,
      { value: string; excludeId?: number }
    >({
      query: ({ value, excludeId }) => {
        const params = new URLSearchParams();
        params.set("value", value);
        if (excludeId != null) params.set("exclude_id", String(excludeId));
        return `${PLATFORM_BASE}/check-email/?${params.toString()}`;
      },
    }),

    updateAdminUser: builder.mutation<PlatformUser, UpdateAdminUserPayload>({
      query: ({ id, ...body }) => ({
        url: `${PLATFORM_BASE}/${id}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [
        "AdminUsers",
        { type: "FileSystem" as const, id: "__root__" },
      ],
    }),

    createAdminUser: builder.mutation<
      PlatformUserCreateResponse,
      CreateAdminUserPayload
    >({
      query: (body) => ({
        url: `${PLATFORM_BASE}/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["AdminUsers"],
    }),

    archiveAdminUser: builder.mutation<
      PlatformUser,
      { id: number; archiveDate?: string | null }
    >({
      query: ({ id, archiveDate }) => ({
        url: `${PLATFORM_BASE}/${id}/archive/`,
        method: "PATCH",
        // archiveDate absent → immediate archive (empty body)
        // archiveDate is string → schedule for that date
        // archiveDate is null → cancel schedule
        body: archiveDate !== undefined ? { archive_date: archiveDate } : {},
      }),
      invalidatesTags: ["AdminUsers"],
    }),

    resetAdminUserPassword: builder.mutation<{ message: string }, number>({
      query: (id) => ({
        url: `${PLATFORM_BASE}/${id}/reset-password/`,
        method: "POST",
      }),
    }),

    generateAdminUserPassword: builder.mutation<
      { generated_password: string },
      number
    >({
      query: (id) => ({
        url: `${PLATFORM_BASE}/${id}/generate-password/`,
        method: "POST",
      }),
    }),

    getAdminUserFolders: builder.query<
      AdminFolderAccess[],
      { id: number; client_id?: number }
    >({
      query: ({ id, client_id }) => {
        const params = client_id ? `?client_id=${client_id}` : "";
        return `${PLATFORM_BASE}/${id}/folders/${params}`;
      },
      providesTags: (_result, _error, { id }) => [
        { type: "AdminUsers", id: `folders-${id}` },
      ],
    }),

    getAssignableFolders: builder.query<
      AdminFolderAccess[],
      number | undefined
    >({
      query: (client_id) => {
        const params = client_id ? `?client_id=${client_id}` : "";
        return `${PLATFORM_BASE}/assignable-folders/${params}`;
      },
      providesTags: ["AdminUsers"],
    }),

    getAssignableIntegrations: builder.query<
      PlatformIntegrationsResponse,
      number | undefined
    >({
      query: (client_id) => {
        const params = client_id ? `?client_id=${client_id}` : "";
        return `${PLATFORM_BASE}/assignable-integrations/${params}`;
      },
      providesTags: ["AdminUsers"],
    }),

    grantFolderAccess: builder.mutation<
      void,
      { userId: number; folderId: number; allowedSubfolders?: string[] }
    >({
      query: ({ userId, folderId, allowedSubfolders }) => ({
        url: `${PLATFORM_BASE}/${userId}/folders/${folderId}/grant/`,
        method: "POST",
        body: { allowed_subfolders: allowedSubfolders ?? [] },
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { type: "AdminUsers", id: `folders-${userId}` },
        "AdminUsers",
      ],
    }),

    updateFolderSubfolders: builder.mutation<
      void,
      { userId: number; folderId: number; allowedSubfolders: string[] }
    >({
      query: ({ userId, folderId, allowedSubfolders }) => ({
        url: `${PLATFORM_BASE}/${userId}/folders/${folderId}/subfolders/`,
        method: "PATCH",
        body: { allowed_subfolders: allowedSubfolders },
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { type: "AdminUsers", id: `folders-${userId}` },
      ],
    }),

    getFolderSubfolders: builder.query<{ subfolders: string[] }, number>({
      query: (folderId) => `${PLATFORM_BASE}/folders/${folderId}/subfolders/`,
    }),

    revokeFolderAccess: builder.mutation<
      void,
      { userId: number; folderId: number }
    >({
      query: ({ userId, folderId }) => ({
        url: `${PLATFORM_BASE}/${userId}/folders/${folderId}/revoke/`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { type: "AdminUsers", id: `folders-${userId}` },
        "AdminUsers",
      ],
    }),

    getAdminUserIntegrations: builder.query<
      PlatformIntegrationsResponse,
      { id: number; client_id?: number }
    >({
      query: ({ id, client_id }) => {
        const params = client_id ? `?client_id=${client_id}` : "";
        return `${PLATFORM_BASE}/${id}/integrations/${params}`;
      },
      providesTags: (_result, _error, { id }) => [
        { type: "AdminUsers", id: `integrations-${id}` },
      ],
    }),

    grantIntegrationAccess: builder.mutation<
      void,
      { userId: number; matchId: number }
    >({
      query: ({ userId, matchId }) => ({
        url: `${PLATFORM_BASE}/${userId}/integrations/${matchId}/grant/`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { type: "AdminUsers", id: `integrations-${userId}` },
        "AdminUsers",
      ],
    }),

    revokeIntegrationAccess: builder.mutation<
      void,
      { userId: number; matchId: number }
    >({
      query: ({ userId, matchId }) => ({
        url: `${PLATFORM_BASE}/${userId}/integrations/${matchId}/revoke/`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { type: "AdminUsers", id: `integrations-${userId}` },
        "AdminUsers",
      ],
    }),

    getAdminChatBotProfile: builder.query<AdminChatBotProfile, number>({
      query: (userId) => `${PLATFORM_BASE}/${userId}/chat-bot-profile/`,
      providesTags: (_result, _error, userId) => [
        { type: "AdminUsers", id: `chat-bot-profile-${userId}` },
      ],
    }),

    createAdminChatBotProfile: builder.mutation<
      AdminChatBotProfile,
      {
        userId: number;
        chat_id?: string;
        api_key?: string;
        name?: string;
        email?: string;
      }
    >({
      query: ({ userId, ...body }) => ({
        url: `${PLATFORM_BASE}/${userId}/chat-bot-profile/`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { type: "AdminUsers", id: `chat-bot-profile-${userId}` },
        "AdminUsers",
      ],
    }),

    updateAdminChatBotProfile: builder.mutation<
      AdminChatBotProfile,
      { userId: number; chat_id?: string; api_key?: string }
    >({
      query: ({ userId, ...body }) => ({
        url: `${PLATFORM_BASE}/${userId}/chat-bot-profile/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { type: "AdminUsers", id: `chat-bot-profile-${userId}` },
      ],
    }),

    updateAdminUserPermissions: builder.mutation<
      PlatformUser,
      {
        id: number;
        is_calibrate_manager: boolean;
        can_download_top_document_folders: boolean;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `${PLATFORM_BASE}/${id}/permissions/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["AdminUsers"],
    }),

    getBackendUsers: builder.query<BackendUsersResponse, GetAdminUsersParams>({
      query: ({ page = 1, search, page_size, sort_by, sort_order } = {}) => {
        const params = new URLSearchParams();
        params.set("page", String(page));
        if (search) params.set("search", search);
        if (page_size) params.set("page_size", String(page_size));
        if (sort_by) params.set("sort_by", sort_by);
        if (sort_order) params.set("sort_order", sort_order);
        return `${BACKEND_BASE}/?${params.toString()}`;
      },
      transformResponse: (response: {
        count: number;
        next: string | null;
        previous: string | null;
        results: Array<{
          id: number;
          username: string;
          first_name: string;
          last_name: string;
          client_name?: string | null;
          client_id?: number | null;
          folder_count: number;
          integration_count: number;
          sftp_connection_count?: number;
          date_joined: string;
          last_login: string | null;
        }>;
      }): BackendUsersResponse => ({
        count: response.count,
        next: response.next,
        previous: response.previous,
        results: response.results.map((u) => ({
          id: u.id,
          username: u.username,
          first_name: u.first_name ?? "",
          last_name: u.last_name ?? "",
          client_name: u.client_name ?? null,
          client_id: u.client_id ?? null,
          folder_count: u.folder_count,
          integration_count: u.integration_count,
          sftp_count: u.sftp_connection_count ?? 0,
          date_joined: u.date_joined,
          last_login: u.last_login,
        })),
      }),
      providesTags: ["AdminUsers"],
    }),

    checkBackendUsernameAvailability: builder.query<
      AvailabilityResponse,
      { value: string; excludeId?: number }
    >({
      query: ({ value, excludeId }) => {
        const params = new URLSearchParams();
        params.set("value", value);
        if (excludeId != null) params.set("exclude_id", String(excludeId));
        return `${BACKEND_BASE}/check-username/?${params.toString()}`;
      },
    }),

    checkBackendEmailAvailability: builder.query<
      AvailabilityResponse,
      { value: string; excludeId?: number }
    >({
      query: ({ value, excludeId }) => {
        const params = new URLSearchParams();
        params.set("value", value);
        if (excludeId != null) params.set("exclude_id", String(excludeId));
        return `${BACKEND_BASE}/check-email/?${params.toString()}`;
      },
    }),

    getBackendUserFolders: builder.query<AdminFolderAccess[], number>({
      query: (id) => `${BACKEND_BASE}/${id}/folders/owned/`,
      providesTags: (_result, _error, id) => [
        { type: "AdminUsers", id: `backend-folders-${id}` },
      ],
    }),

    getBackendUserIntegrations: builder.query<AdminUserIntegrations, number>({
      query: (id) => `${BACKEND_BASE}/${id}/integrations/`,
      providesTags: (_result, _error, id) => [
        { type: "AdminUsers", id: `backend-integrations-${id}` },
      ],
    }),

    createBackendUser: builder.mutation<
      BackendUser,
      {
        username: string;
        first_name: string;
        client_id: number;
      }
    >({
      query: ({ username, first_name, client_id }) => ({
        url: `${BACKEND_BASE}/`,
        method: "POST",
        body: {
          username,
          first_name,
          client_id,
          user_type: "backend",
        },
      }),
      transformResponse: (
        u: PlatformUser & { sftp_connection_count?: number },
      ): BackendUser => ({
        id: u.id,
        username: u.username,
        first_name: u.first_name ?? "",
        last_name: u.last_name ?? "",
        client_name: u.client_name ?? null,
        client_id: u.client_id ?? null,
        folder_count: u.folder_count,
        integration_count: u.integration_count,
        sftp_count: u.sftp_connection_count ?? 0,
        date_joined: u.date_joined,
        last_login: u.last_login,
      }),
      invalidatesTags: ["AdminUsers"],
    }),

    updateBackendUser: builder.mutation<
      BackendUser,
      {
        id: number;
        username?: string;
        first_name?: string;
        client_id?: number;
      }
    >({
      query: ({ id, username, first_name, client_id }) => ({
        url: `${BACKEND_BASE}/${id}/`,
        method: "PATCH",
        body: {
          ...(username !== undefined ? { username } : {}),
          ...(first_name !== undefined ? { first_name } : {}),
          ...(client_id !== undefined ? { client_id } : {}),
        },
      }),
      transformResponse: (
        u: PlatformUser & { sftp_connection_count?: number },
      ): BackendUser => ({
        id: u.id,
        username: u.username,
        first_name: u.first_name ?? "",
        last_name: u.last_name ?? "",
        client_name: u.client_name ?? null,
        client_id: u.client_id ?? null,
        folder_count: u.folder_count,
        integration_count: u.integration_count,
        sftp_count: u.sftp_connection_count ?? 0,
        date_joined: u.date_joined,
        last_login: u.last_login,
      }),
      invalidatesTags: ["AdminUsers"],
    }),

    deleteBackendUser: builder.mutation<
      {
        message: string;
        s3_cleanup_queued?: boolean;
        job_id?: string;
        sftp_connections_removed?: number;
      },
      number
    >({
      query: (id) => ({
        url: `${BACKEND_BASE}/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["AdminUsers"],
    }),
  }),
});

export const {
  useGetAdminUsersQuery,
  useLazyGetAdminUsersQuery,
  useGetAdminUserQuery,
  useLazyCheckPlatformUsernameAvailabilityQuery,
  useLazyCheckPlatformEmailAvailabilityQuery,
  useUpdateAdminUserMutation,
  useCreateAdminUserMutation,
  useArchiveAdminUserMutation,
  useResetAdminUserPasswordMutation,
  useGenerateAdminUserPasswordMutation,
  useGetAdminUserFoldersQuery,
  useGetAssignableFoldersQuery,
  useGetAssignableIntegrationsQuery,
  useGrantFolderAccessMutation,
  useRevokeFolderAccessMutation,
  useUpdateFolderSubfoldersMutation,
  useLazyGetFolderSubfoldersQuery,
  useGetAdminUserIntegrationsQuery,
  useGrantIntegrationAccessMutation,
  useRevokeIntegrationAccessMutation,
  useGetAdminChatBotProfileQuery,
  useCreateAdminChatBotProfileMutation,
  useUpdateAdminChatBotProfileMutation,
  useUpdateAdminUserPermissionsMutation,
  useGetBackendUsersQuery,
  useLazyGetBackendUsersQuery,
  useLazyCheckBackendUsernameAvailabilityQuery,
  useLazyCheckBackendEmailAvailabilityQuery,
  useGetBackendUserFoldersQuery,
  useGetBackendUserIntegrationsQuery,
  useCreateBackendUserMutation,
  useUpdateBackendUserMutation,
  useDeleteBackendUserMutation,
  useGetClientsQuery,
  useGetClientQuery,
  useCreateClientMutation,
  useUpdateClientMutation,
  useArchiveClientMutation,
  useLazyCheckClientCodeAvailabilityQuery,
  useLazyCheckClientNameAvailabilityQuery,
} = adminUsersApi;
